import { createSelector } from '@reduxjs/toolkit';
import { fetchAllDashboardsRequest, TimeRange } from 'actions/dashboard';
import defaultDashboardConfig from 'constants/defaultDashboardConfig';
import emptyDefaultDashboardConfig from 'constants/emptyDefaultDashboardConfig';
import { GlobalState } from 'reducers';
import {
  Dashboard,
  DashboardConfig,
  DashboardFilter,
  DashboardWidget,
  DataNode,
  RouteParams,
  Selector,
  User,
  ValueNode,
} from 'types';
import { navParamsFromTree, Tree } from 'utils/parserTree';

const editDashboard: DashboardConfig = {
  filters: [
    {
      dimension: 'clear',
      type: 'clear',
    },
    {
      dimension: 'unit_of_time_select',
      type: 'unit_of_time_select',
    },
    {
      dimension: 'campaign',
      all_label: 'Use Cases',
      type: 'dropdown',
    },
    {
      dimension: 'team',
      all_label: 'Division',
      type: 'dropdown',
    },
    {
      dimension: 'region',
      all_label: 'Region',
      type: 'dropdown',
    },
    {
      dimension: 'management_level',
      all_label: 'Management Level',
      type: 'dropdown',
    },
    {
      dimension: 'review_value',
      all_label: 'Status',
      type: 'dropdown',
    },
    {
      dimension: 'user',
      all_label: 'Users',
      type: 'dropdown',
    },
    {
      dimension: 'categories',
      all_label: 'Models',
      type: 'dropdown',
    },
    {
      dimension: 'time_range',
    },
  ],
  widgets: [
    {
      widget: 'new_activity_platforms',
      columns_start: 1,
      columns_width: 10,
      rows_start: 3,
      rows_height: 6,
      type: 'sent_at',
      metrics: [
        {
          id: 'activity_1',
          namespace: 'Litlingo',
          metric: 'total',
          label: 'Messages',
          // @ts-ignore
          opensearch_enabled: true,
        },
        {
          id: 'activity_1_platform_o365',
          namespace: 'Litlingo',
          metric: 'platform_a',
          label: 'Messages Outlook',
          icon: 'outlook',
          // @ts-ignore
          opensearch_enabled: true,
        },
        {
          id: 'actibity_1_platform_teams',
          namespace: 'Litlingo',
          metric: 'platform_b',
          label: 'Messages Teams',
          icon: 'teams',
          // @ts-ignore
          opensearch_enabled: true,
        },
        {
          id: 'activity_2',
          namespace: 'Litlingo',
          metric: 'total_has_events',
          label: 'Events',
          dimensions: [
            {
              name: 'rule',
              value: '8d73dd3c-4a6c-45ff-a994-135c9eb615aa',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: 'ed78ec90-9df5-44fb-bd11-70d414c246a9',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '05833851-da28-464e-bf3b-614859b6534e',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '29732af7-f941-4ae7-b643-42fd2d538e0a',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '2e7d5882-e3d6-43fb-a82c-a84def68b9f0',
              logical_operator: 'OR',
            },
          ],
          // @ts-ignore
          opensearch_enabled: true,
        },
        {
          id: 'activity_2_platform_o365',
          namespace: 'Litlingo',
          metric: 'platform_a',
          label: 'Events Outlook',
          icon: 'outlook',
          dimensions: [
            {
              name: 'rule',
              value: '8d73dd3c-4a6c-45ff-a994-135c9eb615aa',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: 'ed78ec90-9df5-44fb-bd11-70d414c246a9',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '05833851-da28-464e-bf3b-614859b6534e',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '29732af7-f941-4ae7-b643-42fd2d538e0a',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '2e7d5882-e3d6-43fb-a82c-a84def68b9f0',
              logical_operator: 'OR',
            },
          ],
          // @ts-ignore
          opensearch_enabled: true,
        },
        {
          id: 'activity_2_platform_teams',
          namespace: 'Litlingo',
          metric: 'platform_b',
          label: 'Events Teams',
          icon: 'teams',
          dimensions: [
            {
              name: 'rule',
              value: '8d73dd3c-4a6c-45ff-a994-135c9eb615aa',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: 'ed78ec90-9df5-44fb-bd11-70d414c246a9',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '05833851-da28-464e-bf3b-614859b6534e',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '29732af7-f941-4ae7-b643-42fd2d538e0a',
              logical_operator: 'OR',
            },
            {
              name: 'rule',
              value: '2e7d5882-e3d6-43fb-a82c-a84def68b9f0',
              logical_operator: 'OR',
            },
          ],
          // @ts-ignore
          opensearch_enabled: true,
        },
      ],
      title: '',
      container_class_name: 'empty',
    },
  ],
  override_start_datetime: '',
  override_time_range: '',
  cache: false,
  rollup: false,
  definitions: {
    t1: {
      allow: ['b367f439-4522-421f-8142-3e0ed76d5c8e'],
      disallow: ['6fec57c0-52e9-42b5-95a4-7c0e9a445652'],
    },
    t2: {
      allow: ['b367f439-4522-421f-8142-3e0ed76d5c8e'],
      disallow: ['6fec57c0-52e9-42b5-95a4-7c0e9a445652'],
    },
    t3: {
      allow: ['b367f439-4522-421f-8142-3e0ed76d5c8e'],
      disallow: ['6fec57c0-52e9-42b5-95a4-7c0e9a445652'],
    },
    platform_a: 'o365',
    platform_b: 'o365_teams',
  },
  primary_timezone: 'America/Chicago',
};

export const getCustomerDashboardConfig =
  (activeUuid: string | undefined): Selector<DashboardConfig & { key: string }> =>
  ({ auth }): DashboardConfig & { key: string } => {
    if (activeUuid && auth.user.customer?.dashboards) {
      const dashboard = auth.user.customer?.dashboards[activeUuid];

      if (dashboard && dashboard.config) {
        return {
          key: activeUuid,
          ...(dashboard.config as DashboardConfig),
        };
      }
    }
    // use to develop from default
    // return { key: 'global_default', ...defaultDashboardConfig };

    function getDefaultFilters(filter: DashboardFilter): DashboardFilter {
      const defaultCustomerFilter = auth.user.customer?.config.dashboard_config?.filters?.find(
        (fil) => fil.dimension === filter.dimension
      );

      return {
        ...filter,
        default_value:
          defaultCustomerFilter?.default_value !== null
            ? defaultCustomerFilter?.default_value
            : undefined,
      };
    }

    if (auth.user.customer?.config.dashboard_config != null) {
      if (auth.user.customer?.config.dashboard_config?.filters) {
        return {
          key: 'customer_default',
          ...auth.user.customer?.config.dashboard_config,
          filters: [
            ...(auth.user.customer?.config.dashboard_config?.filters as DashboardFilter[]).map(
              (filter) => getDefaultFilters(filter)
            ),
          ],
        };
      }
      return {
        key: 'customer_default',
        ...auth.user.customer?.config.dashboard_config,
      };
    }

    return { key: 'global_empty_default', ...emptyDefaultDashboardConfig };
  };

export const getWidgetFiltersById =
  (id: string, activeUuid: string | null): Selector<DashboardFilter[] | undefined> =>
  ({ auth }): DashboardFilter[] | undefined => {
    if (auth.user.customer?.dashboards && activeUuid) {
      const dashboard = auth.user.customer?.dashboards[activeUuid];
      const widgetItem = dashboard.config?.widgets.find(({ type, widget }, currentIndex) => {
        const widgetId =
          type != null
            ? `${widget}--${type}--${currentIndex}--${activeUuid}`
            : `${widget}--${currentIndex}--${activeUuid}`;

        return widgetId === id;
      });
      return widgetItem?.filters;
    }
    let widgetItem = defaultDashboardConfig.widgets.find(({ widget, type }, index) => {
      const widgetId =
        type != null
          ? `${widget}--${type}--${index}--global_default`
          : `${widget}--${index}--global_default`;
      return id === widgetId;
    });
    if (widgetItem) {
      return widgetItem?.filters;
    }

    widgetItem = auth.user.customer?.config.dashboard_config?.widgets.find(
      ({ widget, type }, index) => {
        const widgetId =
          type != null
            ? `${widget}--${type}--${index}--customer_default`
            : `${widget}--${index}--customer_default`;
        return id === widgetId;
      }
    );
    if (widgetItem) {
      return widgetItem?.filters;
    }
    return undefined;
  };

export const getActiveUuid: Selector<string | null> = ({ dashboard }) => dashboard.activeUuid;

export const getLoadedDashboards: Selector<Dashboard[]> = ({ dashboard }) =>
  Object.values(dashboard.dashboards);

export const getDashboardsLoading: Selector<boolean> = (state) =>
  state.dashboard.loading.includes(fetchAllDashboardsRequest.toString());

export const getDashboardTimeRange: Selector<TimeRange> = (state) => state.dashboard.timeRange;

export const getUser = (state: GlobalState): User => state.auth.user;

type ReturnDashboarState = (
  state: GlobalState
) => { key: string; user: User; name: string } & DashboardConfig;

export const getDashboardState = (activeUuid?: string): ReturnDashboarState =>
  createSelector(
    [
      getUser,
      (state: GlobalState): boolean => state.dashboardEdit.isEditing,
      (state: GlobalState): DashboardWidget | null => state.dashboardEdit.widget,
    ],
    (user, isEditing, widget) => {
      if (isEditing && widget) {
        return {
          key: 'edit-dashboard',
          user,
          name: 'edit-dashboard',
          ...(editDashboard as DashboardConfig),
          widgets: [{ ...widget, columns_start: 1, rows_start: 1 }],
        };
      }

      if (activeUuid && user.customer?.dashboards) {
        const dashboard = user.customer?.dashboards[activeUuid];

        if (dashboard && dashboard.config) {
          return {
            key: activeUuid,
            user,
            name: dashboard.name,
            ...(dashboard.config as DashboardConfig),
          };
        }
      }

      // use to develop from default
      // return { key: 'global_default', user, ...defaultDashboardConfig };

      function getDefaultFilters(filter: DashboardFilter): DashboardFilter {
        const defaultCustomerFilter = user.customer?.config.dashboard_config?.filters?.find(
          (fil) => fil.dimension === filter.dimension
        );

        return {
          ...filter,
          default_value:
            defaultCustomerFilter?.default_value !== null
              ? defaultCustomerFilter?.default_value
              : undefined,
        };
      }

      if (user.customer?.config.dashboard_config != null) {
        if (user.customer?.config.dashboard_config?.filters) {
          return {
            key: 'customer_default',
            user,
            name: 'default',
            ...user.customer?.config.dashboard_config,
            filters: [
              ...(user.customer?.config.dashboard_config?.filters as DashboardFilter[]).map(
                (filter) => getDefaultFilters(filter)
              ),
            ],
          };
        }
        return {
          key: 'customer_default',
          user,
          name: 'default',
          ...user.customer?.config.dashboard_config,
        };
      }

      return {
        key: 'global_empty_default',
        user,
        name: 'global-default',
        ...emptyDefaultDashboardConfig,
      };
    }
  );

export const getIsEditingWidget: Selector<boolean> = (state) => state.dashboardEdit.isEditing;

export const getNavParamsFromTree: Selector<RouteParams> = createSelector(
  [
    (state: GlobalState): DashboardWidget | null => state.dashboardEdit.widget,
    (state: GlobalState): number | null => state.dashboardEdit.metricEditIndex,
  ],
  (widget, metricIndex) => {
    if (widget) {
      return navParamsFromTree(
        // @ts-ignore
        metricIndex !== null ? widget.filters_search[metricIndex] : widget.filters_search[0] || ''
      );
    }
    return {};
  }
);

export const getFiltersSearchString: Selector<string, [number]> = (state, metricIndex) => {
  const filtersSearch =
    metricIndex !== null
      ? state.dashboardEdit.widget?.filters_search[metricIndex]
      : state.dashboardEdit.widget?.filters_search[0];
  return filtersSearch || '';
};

export const getTree: Selector<Tree, [number]> = (state, metricIndex) =>
  metricIndex !== null
    ? state.dashboardEdit.filtersTree[metricIndex]
    : state.dashboardEdit.filtersTree[0];

export const getEditWidget: Selector<DashboardWidget | null> = (state) =>
  state.dashboardEdit.widget;

export const getEditWidgetIndex: Selector<number> = (state) => state.dashboardEdit.widgetIndex;

export const getSelectedFilters: Selector<ValueNode[]> = (state) =>
  state.dashboardEdit.selectedFilters;

export const getSelectedFields: Selector<DataNode[]> = (state) =>
  state.dashboardEdit.selectedFields;

export const getMetricEditIndex: Selector<number | null> = (state) =>
  state.dashboardEdit.metricEditIndex;

export default getCustomerDashboardConfig;
